import React from "react";
import { LogOut } from "lucide-react";
import Seo from "@/components/common/Seo";
import TillInstallBanner from "@/components/partner/TillInstallBanner";
import TillOfflineBar from "@/components/partner/TillOfflineBar";
import { useStaffTill } from "@/features/staffTill/useStaffTill";
import TillLogin from "@/features/staffTill/TillLogin";
import SessionsCard from "@/features/staffTill/SessionsCard";
import EarningsCard from "@/features/staffTill/EarningsCard";
import PaymentAnnouncer from "@/components/engage/PaymentAnnouncer";
import { tillApi } from "@/features/staffTill/helpers";

/**
 * Iter-325 — staff-facing till page (/till). NO owner login: staff sign in
 * with the vendor ID + their 4-digit PIN.
 *   POST /engagement/staff/login {vendor_id, pin} → {access_token, staff}
 *   GET  /engagement/till/sessions      (5s poll)
 *   PUT  /engagement/till/sessions/{id}/bill {bill_amount}
 *   GET  /engagement/till/earnings      (view_earnings_ledger only)
 * Uses its own axios client (staff bearer token, sessionStorage) so the
 * customer auth interceptors never interfere.
 *
 * Iter 639 — split (behaviour IDENTICAL, every testid unchanged): all
 * page state lives in features/staffTill/useStaffTill.js which returns
 * ONE bag (`const t = useStaffTill()`); every card takes `{ t }`. The
 * axios client + utils live in features/staffTill/helpers.js. This file
 * is only the layout shell.
 */
export default function TillPage() {
  const t = useStaffTill();

  return (
    <div className="min-h-screen bg-slate-100">
      <Seo title="Till · Allsale Classified" description="Staff till — enter customer bills and manage scan-and-save sessions." />
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Iter-450 — offline notice for mid-shift network drops. */}
        <TillOfflineBar />
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-black text-slate-900" data-testid="till-title">Till</h1>
          {t.staff && (
            <button type="button" onClick={t.logout} data-testid="till-logout"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800">
              <LogOut className="w-3.5 h-3.5" /> {t.staff.name} · Log out
            </button>
          )}
        </div>
        {!t.staff ? (
          <>
            <TillLogin t={t} />
            {/* Iter-416 — install banner on the till login. */}
            <TillInstallBanner />
          </>
        ) : (
          <TillWorkspace t={t} />
        )}
      </div>
    </div>
  );
}

function TillWorkspace({ t }) {
  return (
    <div className="mt-6">
      {/* Iter 660 — spoken payment announcements, under the staff card
          above Live sessions. Per-device toggle; language is store-level. */}
      <div className="mb-4">
        <PaymentAnnouncer
          prefix="till-announce"
          storageKey="announce:till"
          fetchFeed={async () =>
            (await tillApi.get("/classifieds/engagement/till/paid-feed")).data
          }
          requestTts={async (session_id, lang) =>
            (await tillApi.post("/classifieds/engagement/announce/tts", { session_id, lang })).data
          }
        />
      </div>
      {t.canEarnings && (
        <div className="flex gap-2">
          <TabBtn active={t.tab === "sessions"} onClick={() => t.setTab("sessions")} testid="till-tab-sessions">Sessions</TabBtn>
          <TabBtn active={t.tab === "earnings"} onClick={() => t.setTab("earnings")} testid="till-tab-earnings">Earnings</TabBtn>
        </div>
      )}
      {t.tab === "sessions" ? (
        <SessionsCard t={t} />
      ) : (
        <EarningsCard t={t} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-full text-xs font-bold px-4 py-2 transition-colors ${
        active ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50 border border-slate-200"
      }`}>
      {children}
    </button>
  );
}
